import {useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Skeleton,
  Text,
  useBreakpointValue,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {useRangeScroll} from '@amblique/king-living/app/hooks/use-range-scroll';
import {isNearPageBottom} from '@amblique/king-living/app/utils/range-scroll-utils';
import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {ShowroomListHeader} from '@amblique/king-living/app/components/showroom/showroom-list-header';
import {CalloutBlockOverlayCF024} from '@amblique/king-living/app/contentful/models/hero-banner/callout-block-overlay-cf024';
import {CalloutBlockSplitCF023} from '@amblique/king-living/app/contentful/models/hero-banner/callout-block-split-cf023';
import {HERO_BANNER_APPEARANCE} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-appearance';
import {HeroBannerBlockCF01} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-block-cf01';
import {HeroBannerBlockCF04} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-block-cf04';
import {HeroBannerBlockCF06} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-block-cf06';

/**
 * Secondary navigation bar that appears when the user scrolls past the Hero Banner.
 *
 * Isolated into its own mini-component so that only this leaf component re-renders
 * when `isHeaderHidden` changes on every scroll event — the parent HeroBanner (which
 * renders heavy media) stays unaffected.
 *
 * Desktop: fixed to top; past CTAs keeps nav visible; scroll up stacks header above nav.
 * Mobile:  fixed to bottom; past CTAs shows nav while header is hidden, or when near page bottom
 *           (keeps nav visible after overscroll bounce at end of page).
 *
 * @param {Object} props
 * @param {boolean} props.isPastCTAs - Whether the hero has left the viewport
 * @param {string} [props.headingTitle] - Product name shown on the left
 * @param {Object} [props.cta] - Contentful Link component for primary CTA
 * @param {Object} [props.rightCta] - Contentful Link component for secondary CTA
 * @param {Object} props.styles - Styles from useMultiStyleConfig
 */
const RangeSecondaryNav = ({isPastCTAs, headingTitle, cta, rightCta, styles}) => {
  const {isHeaderHidden, scrollY} = useHeaderContext();
  const isDesktop = useBreakpointValue({base: false, lg: true});

  const isNearPageBottomValue = useMemo(() => isNearPageBottom(scrollY), [scrollY]);

  // Desktop: past CTAs keeps nav visible; scroll up stacks header above nav.
  // Mobile: header hidden OR near page bottom (fallback if header reveal is not suppressed).
  const isVisible =
    isPastCTAs &&
    (isDesktop || isHeaderHidden || (!isDesktop && isNearPageBottomValue));

  return (
    <Flex
      className="hero-banner__secondary-nav"
      {...styles.secondaryNav}
      opacity={isVisible ? 1 : 0}
      pointerEvents={isVisible ? 'auto' : 'none'}
      top={{base: 'auto', lg: isHeaderHidden ? 0 : 'var(--header-height)'}}
      transform={{
        base: isVisible ? 'translateY(0)' : 'translateY(110%)',
        lg: isVisible ? 'translateY(0)' : 'translateY(-110%)',
      }}
    >
      <Flex className="hero-banner__secondary-nav-content" {...styles.secondaryNavContent}>
        {headingTitle && <Text {...styles.secondaryNavTitle}>{headingTitle}</Text>}
        <Flex className="hero-banner__secondary-nav-ctas" {...styles.secondaryNavCtas}>
          <Box className="hero-banner__secondary-nav-cta hero-banner__secondary-nav-cta-left">{cta && <Component component={cta} />}</Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

RangeSecondaryNav.displayName = 'RangeSecondaryNav';
RangeSecondaryNav.propTypes = {
  isPastCTAs: PropTypes.bool.isRequired,
  headingTitle: PropTypes.string,
  cta: PropTypes.object,
  rightCta: PropTypes.object,
  styles: PropTypes.object.isRequired,
};

/**
 * HeroBanner component
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const HeroBanner = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {appearance, enableScrollEffect, headingTitle, cta, rightCta} = component;

  // Observe hero leaving viewport — only active when enableScrollEffect is true.
  const heroRef = useRef(null);
  const {isPastCTAs} = useRangeScroll(heroRef, {enabled: !!enableScrollEffect});

  const {setHideOnScroll} = useHeaderContext();

  useEffect(() => {
    if (!enableScrollEffect) return;
    setHideOnScroll(true);

    return () => setHideOnScroll(false);
  }, [enableScrollEffect, setHideOnScroll]);

  const styles = useMultiStyleConfig('contentful/hero-banner', {
    component,
    isComplete,
    isFetching,
    variant: appearance,
  });

  // Page slim fragment only resolves sys.id — full entry fetch runs client-side.
  // Avoid NotImplemented (model name + loading badges) until appearance is known.
  if (!isComplete) {
    if (isFetching) {
      return (
        <Box className="hero-banner hero-banner--loading" {...styles.root} {...props}>
          <Skeleton aspectRatio={{base: '4/5', lg: '2/1'}} width="100%" />
        </Box>
      );
    }

    return null;
  }

  return (
    <Box ref={heroRef} {...styles.root} {...props}>
      {appearance === HERO_BANNER_APPEARANCE.CF_01 ? (
        <HeroBannerBlockCF01 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === HERO_BANNER_APPEARANCE.CF_04 ? (
        <HeroBannerBlockCF04 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === HERO_BANNER_APPEARANCE.CF_06 ? (
        <HeroBannerBlockCF06 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === HERO_BANNER_APPEARANCE.CALLOUT_SPLIT_CF_023 ? (
        <CalloutBlockSplitCF023 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === HERO_BANNER_APPEARANCE.CALLOUT_OVERLAY_CF_024 ? (
        <CalloutBlockOverlayCF024 component={component} isComplete={isComplete} isFetching={isFetching} styles={styles} />
      ) : appearance === HERO_BANNER_APPEARANCE.SHOWROOMS_HEADER_CF_032 ? (
        <ShowroomListHeader heroData={component} />
      ) : (
        <NotImplemented component={component} isComplete={isComplete} isFetching={isFetching} />
      )}

      {enableScrollEffect && (
        <RangeSecondaryNav
          cta={cta}
          headingTitle={headingTitle}
          isPastCTAs={isPastCTAs}
          rightCta={rightCta}
          styles={styles}
        />
      )}
    </Box>
  );
};

HeroBanner.displayName = 'HeroBanner';
HeroBanner.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
